<template>
  <div class="app-chat-container">
    <transition mode="out-in" name="chat-window">
      <chat-window
        v-if="chatWindowVisible"
        :sessions="chatSessions"
        class="app-chat-window"
        :class="{ chatWindowExpanded }"
        @send-message="onSendMessage"
        @select-suggestion="onSelectSuggestion"
        @accept-action="onAcceptAction"
        @chat-close="onToggleVisible"
        @chat-expand-toggle="onToggleExpand"
      />
    </transition>
    <button class="app-chat-button" @click="onToggleVisible">
      <img v-if="!chatWindowVisible" src="/img/rready-ai-icon.svg" />
      <img v-else src="/img/xmark.svg" />
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { IUser } from '@rready/sdk';
import type {
  IChatSession,
  IContextManager,
  OnAcceptAction,
  OnSelectSuggestion,
  OnUserMessage
} from '@rready/components/src/chat/chatTypes';
import { useAiChatProvider } from '@rready/components/src/chat';
import { useAiAgentStore } from '../../store/aiAgentStore';
import { useChatStore } from '../../store/chatStore';
import { useAuthStore } from '../../store/authStore';

export default defineComponent({
  data() {
    return {
      chatWindowVisible: false,
      chatWindowExpanded: false
    };
  },
  watch: {
    chatWindowVisible(val: boolean) {
      if (val) {
        this.initChatSessions();
      }
    }
  },
  computed: {
    agentStore() {
      return useAiAgentStore();
    },
    chatStore() {
      return useChatStore();
    },
    currentUser(): IUser {
      return useAuthStore().user?.raw;
    },
    chatSessions(): IChatSession[] {
      if (!this.chatWindowVisible) {
        return [];
      }
      const { pageAgents } = this.agentStore;
      const { loadSession } = this.chatStore;
      return pageAgents.map((it) => loadSession(it.id));
    }
  },
  methods: {
    initChatSessions(): void {
      const { pageAgents } = useAiAgentStore();
      pageAgents.forEach((agent) => {
        const { hasSession, createSession } = useAiChatProvider(
          this.$gateway.ai,
          this.$res,
          agent,
          this.currentUser,
          this.agentStore as unknown as IContextManager,
          this.chatStore
        );
        if (!hasSession(agent.id)) {
          createSession(agent.id);
        }
      });
    },
    async onSendMessage(message: OnUserMessage): Promise<void> {
      const agent = useAiAgentStore().getAgent(message.sessionKey);
      const { openSession } = useAiChatProvider(
        this.$gateway.ai,
        this.$res,
        agent!,
        this.currentUser,
        this.agentStore as unknown as IContextManager,
        this.chatStore
      );
      const { sendMessage } = openSession(message.sessionKey);
      await sendMessage(message.text);
    },
    async onSelectSuggestion(message: OnSelectSuggestion): Promise<void> {
      const res = this.$res(
        useAiAgentStore().getAgent(message.sessionKey)?.messages
      );
      const msg = res.suggestedPrompts[message.suggestionIndex];
      this.onSendMessage({
        sessionKey: message.sessionKey,
        text: msg.prompt
      });
    },
    async onAcceptAction(message: OnAcceptAction) {
      const agent = useAiAgentStore().getAgent(message.sessionKey);
      const { openSession } = useAiChatProvider(
        this.$gateway.ai,
        this.$res,
        agent!,
        this.currentUser,
        this.agentStore as unknown as IContextManager,
        this.chatStore
      );
      const { acceptAction } = openSession(message.sessionKey);
      await acceptAction(message.message, message.sessionKey);
    },
    onToggleVisible(): void {
      this.chatWindowVisible = !this.chatWindowVisible;
    },
    onToggleExpand(expanded: boolean): void {
      this.chatWindowExpanded = expanded;
    }
  }
});
</script>

<style lang="scss" scoped>
.app-chat-container {
  position: fixed;
  bottom: 60px;
  right: 100px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 20px;
}
.app-chat-window {
  --max-height: calc(100vh - calc(30px + 64px + 20px) - 30px);
  width: 450px;
  height: calc(var(--max-height) - 100px);
  max-width: calc(100vw - 200px);
  max-height: var(--max-height);
  transition:
    width 0.2s ease-out,
    height 0.2s ease-out;
}
.chatWindowExpanded {
  width: 100vw;
  height: 100vh;
}
.app-chat-button {
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  border-radius: 64px;
  background: radial-gradient(
    circle at top right,
    rgba(180, 120, 255, 1) 10%,
    rgba(121, 16, 255, 1) 70%,
    rgba(25, 2, 55, 1) 90%
  );
  transition:
    transform 0.3s,
    box-shadow 0.3s;

  img {
    width: 30px;
    height: 30px;
  }
}

.app-chat-button:hover {
  transform: scale(1.02);
  box-shadow: 0 0 12px 3px rgba(180, 120, 255, 0.3);
}

/* chat window transition */

.chat-window-enter {
  transform: translate(5px, 10px);
  opacity: 0;
}

.chat-window-enter-active {
  transition: all 0.2s ease-out;
}

.chat-window-leave-active {
  transition: all 0.2s ease-in;
}

.chat-window-leave-to {
  transform: translate(5px, 10px);
  opacity: 0;
}
</style>
